/**
 * MobileSportsBar Component
 * Horizontal scrollable sports navigation bar for mobile devices
 *
 * Following SOLID principles:
 * - Single Responsibility: Only handles mobile sports navigation
 * - Open/Closed: Extensible through sport links configuration
 * - Liskov Substitution: Can be used anywhere sport navigation is needed
 * - Interface Segregation: Minimal prop interface
 * - Dependency Inversion: Depends on sport links abstraction
 */

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getSportLinks } from '../../config/sportLinks';

/**
 * Mobile Sports Navigation Bar
 * Displays sport links in a horizontal scrollable bar
 *
 * @param {Object} props
 * @param {string} [props.className] - Optional additional CSS classes
 */
const MobileSportsBar = ({ className = '' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const sportLinks = getSportLinks();

  /**
   * Handle sport navigation
   * @param {Object} sport - Sport link object
   */
  const handleSportClick = (sport) => {
    if (sport.status === 'coming-soon') {
      navigate('/chat');
    } else {
      navigate(sport.path);
    }
  };

  /**
   * Check if a sport is currently active
   * @param {string} path - Sport path
   * @returns {boolean}
   */
  const isActiveSport = (path) => {
    return location.pathname === path;
  };

  return (
    <div className={`w-full bg-black/95 backdrop-blur-lg border-b border-white/[0.05] ${className}`}>
      <div className="px-3 py-2">
        {/* Sports Navigation Bar */}
        <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">

          {/* Sport Buttons */}
          {sportLinks.map((sport) => {
            const isActive = isActiveSport(sport.path);
            const isActiveStatus = sport.status === 'active';
            const isComingSoon = sport.status === 'coming-soon';

            return (
              <button
                key={sport.id}
                onClick={() => handleSportClick(sport)}
                disabled={!isActiveStatus && !isComingSoon}
                className={`
                  flex-shrink-0 px-3 py-1.5 rounded-lg
                  transition-all duration-200
                  font-mono text-[10px] tracking-wide
                  text-xs
                  border
                  ${isActive
                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400/90'
                    : isActiveStatus
                    ? 'bg-white/[0.02] border-white/[0.05] text-white/60 hover:bg-white/[0.05] hover:border-white/[0.1] hover:text-white/80'
                    : isComingSoon
                    ? 'bg-white/[0.01] border-white/[0.03] text-white/30 hover:text-white/40'
                    : 'bg-white/[0.01] border-white/[0.02] text-white/20 cursor-not-allowed'
                  }
                  active:scale-95
                `}
                aria-label={`Navigate to ${sport.label}`}
                aria-current={isActive ? 'page' : undefined}
              >
                {sport.label}
              </button>
            );
          })}

          {/* Scroll indicator - shows there's more content */}
          <div className="flex-shrink-0 w-8 flex items-center justify-center">
            <div className="w-1 h-1 rounded-full bg-white/10" />
          </div>
        </div>
      </div>

      {/* Add custom scrollbar styles */}
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default MobileSportsBar;
